import * as XLSX from 'xlsx'
import { IMPLICIT_DEADLINE_MS, combinedReactionStats, isValidImplicitTrial, reactionStats } from './implicitTestAnalysis'

export const GEN_Z_SURVEY_CODES = [
  'gen_z_mk_neuro',
  'gen_z_rs_neuro',
  'gen_z_xk_neuro',
  'gen_z_me_neuro',
]

export function genZImplicitRuns(response) {
  return (Array.isArray(response?.advertisement_tests) ? response.advertisement_tests : [])
    .filter((advertisement) => Array.isArray(advertisement.implicit_trials))
    .map((advertisement) => ({
      advertisement_id: advertisement.advertisement_id,
      trials: advertisement.implicit_trials,
    }))
}

export function genZAttributeStats(trials) {
  const groups = (Array.isArray(trials) ? trials : []).reduce((result, trial) => {
    const key = trial.attribute || String(trial.attribute_index || '')
    if (!key) return result
    if (!result[key]) result[key] = []
    result[key].push(trial)
    return result
  }, {})
  return Object.entries(groups).map(([attribute, attributeTrials]) => ({
    attribute,
    ...reactionStats(attributeTrials),
    matchCount: attributeTrials.filter((trial) => trial.response === 'matches').length,
    doesNotMatchCount: attributeTrials.filter((trial) => trial.response === 'does_not_match').length,
    stimuli: attributeTrials.map((trial) => trial.stimulus).filter(Boolean).join(' · '),
  }))
}

function responseId(response) {
  return response.response_id || response.source_response_id || response.id || ''
}

function reportStatus(response) {
  return response.reportActive === false ? 'Pasif' : 'Aktif'
}

function sheet(rows) {
  const result = XLSX.utils.json_to_sheet(rows)
  if (rows.length) {
    result['!autofilter'] = {
      ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: Object.keys(rows[0]).length - 1, r: rows.length } }),
    }
  }
  return result
}

export function exportGenZImplicitExcel(responses, surveyCode) {
  const completed = responses.filter((response) => response.status === 'completed' || genZImplicitRuns(response).length)
  const active = completed.filter((response) => response.reportActive !== false)
  const allRuns = active.flatMap(genZImplicitRuns)
  const byAdvertisement = allRuns.reduce((groups, run) => {
    const id = run.advertisement_id || 'unknown'
    if (!groups[id]) groups[id] = []
    groups[id].push(run)
    return groups
  }, {})
  const summaryRow = (label, runs, participantCount) => {
    const stats = combinedReactionStats(runs)
    return {
      Kapsam: label,
      'Aktif katılımcı': participantCount,
      'Reklam gösterimi': runs.length,
      'Toplam deneme': stats.trialCount,
      'Geçerli tepki': stats.validCount,
      'Ortalama geçerli tepki süresi (ms)': stats.validAverageRt,
      'Medyan geçerli tepki süresi (ms)': stats.medianRt,
      'Eşleşiyor oranı (%)': stats.validMatchesPercent,
      'Zamanında yanıt oranı (%)': stats.onTimePercent,
    }
  }
  const summary = [
    summaryRow('Tüm reklamlar', allRuns, active.length),
    ...Object.entries(byAdvertisement).map(([id, runs]) => summaryRow(id, runs, runs.length)),
  ]
  const participants = completed.map((response) => {
    const runs = genZImplicitRuns(response)
    const stats = combinedReactionStats(runs)
    return {
      'Yanıt ID': responseId(response),
      Ülke: response.country || '',
      'Rapor durumu': reportStatus(response),
      'Reklam gösterimi': runs.length,
      'Toplam deneme': stats.trialCount,
      'Geçerli tepki': stats.validCount,
      'Ortalama geçerli tepki süresi (ms)': stats.validAverageRt,
      'Medyan geçerli tepki süresi (ms)': stats.medianRt,
      'Eşleşiyor oranı (%)': stats.validMatchesPercent,
      'Zamanında yanıt oranı (%)': stats.onTimePercent,
    }
  })
  const advertisements = completed.flatMap((response) => genZImplicitRuns(response).map((run) => {
    const stats = reactionStats(run.trials)
    return {
      'Yanıt ID': responseId(response),
      'Rapor durumu': reportStatus(response),
      Reklam: run.advertisement_id || '',
      'Toplam deneme': stats.trialCount,
      'Geçerli tepki': stats.validCount,
      'Ortalama geçerli tepki süresi (ms)': stats.validAverageRt,
      'Medyan geçerli tepki süresi (ms)': stats.medianRt,
      'Eşleşiyor oranı (%)': stats.validMatchesPercent,
      'Zamanında yanıt oranı (%)': stats.onTimePercent,
    }
  }))
  const attributes = completed.flatMap((response) => genZImplicitRuns(response).flatMap((run) =>
    genZAttributeStats(run.trials).map((attribute) => ({
      'Yanıt ID': responseId(response),
      'Rapor durumu': reportStatus(response),
      Reklam: run.advertisement_id || '',
      Özellik: attribute.attribute,
      İfadeler: attribute.stimuli,
      'Geçerli tepki': attribute.validCount,
      'Ortalama geçerli tepki süresi (ms)': attribute.validAverageRt,
      'Medyan geçerli tepki süresi (ms)': attribute.medianRt,
      'Eşleşiyor sayısı': attribute.matchCount,
      'Eşleşmiyor sayısı': attribute.doesNotMatchCount,
    }))
  ))
  const trials = completed.flatMap((response) => genZImplicitRuns(response).flatMap((run) =>
    run.trials.map((trial, index) => ({
      'Yanıt ID': responseId(response),
      'Rapor durumu': reportStatus(response),
      Reklam: run.advertisement_id || '',
      'Deneme sırası': index + 1,
      Özellik: trial.attribute || '',
      İfade: trial.stimulus || '',
      Yanıt: trial.response === 'matches' ? 'Eşleşiyor' : trial.response === 'does_not_match' ? 'Eşleşmiyor' : '',
      'Tepki süresi (ms)': trial.rt_ms !== null && trial.rt_ms !== undefined && trial.rt_ms !== '' && Number.isFinite(Number(trial.rt_ms)) ? Number(trial.rt_ms) : null,
      'Zaman aşımı': trial.timed_out === true ? 'Evet' : 'Hayır',
      'Analize dahil': isValidImplicitTrial(trial) ? 'Evet' : 'Hayır',
    }))
  ))

  const workbook = XLSX.utils.book_new()
  const method = [
    { Metrik: 'Toplu analiz', Tanım: 'Yalnızca rapor durumu Aktif olan tamamlanmış katılımcılar özetlere dahil edilir. Pasif katılımcıların denemeleri ayrıntı sayfalarında korunur.' },
    { Metrik: 'Geçerli tepki', Tanım: `Yanıtı bulunan, zaman aşımı işaretli olmayan ve tepki süresi ${IMPLICIT_DEADLINE_MS} ms veya altında olan deneme.` },
    { Metrik: 'Tepki süresi', Tanım: 'Reklamdan sonra gösterilen kelimenin görünmesinden yanıta kadar geçen süre, milisaniye cinsinden.' },
    { Metrik: 'IAT D-skoru', Tanım: 'Bu testte karşıt eşleştirme blokları bulunmadığı için klasik IAT D-skoru hesaplanmaz.' },
  ]
  ;[
    ['Özet', summary],
    ['Katılımcılar', participants],
    ['Reklam sonuçları', advertisements],
    ['Özellik analizi', attributes],
    ['Ham denemeler', trials],
    ['Metodoloji', method],
  ].forEach(([name, rows]) => XLSX.utils.book_append_sheet(workbook, sheet(rows), name))
  XLSX.writeFile(workbook, `${surveyCode}-implicit-detay-raporu.xlsx`)
}
